import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { Modal } from '../components/Modal';
import type { StaffUser } from '../types';
import { useAuth } from '../auth';

// No ambiguous characters (0/O, 1/l/I) — these get read out loud or typed
// from a WhatsApp message.
const PASSWORD_CHARS = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
const generatePassword = (length = 14): string => {
  const buf = new Uint32Array(length);
  crypto.getRandomValues(buf);
  return [...buf].map((n) => PASSWORD_CHARS[n % PASSWORD_CHARS.length]).join('');
};

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });

// Shown once after creating a user / resetting a password so the admin can
// hand the credential over; it is not retrievable afterwards.
type IssuedCredential = { email: string; password: string; kind: 'created' | 'reset' };

const PasswordField = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="field">
    <label>Password (min 8 chars)</label>
    <div style={{ display: 'flex', gap: 8 }}>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={{ flex: 1 }} />
      <button type="button" onClick={() => onChange(generatePassword())}>
        Generate
      </button>
    </div>
  </div>
);

const AddUserModal = ({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (cred: IssuedCredential) => Promise<void>;
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(generatePassword());
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = name.trim() !== '' && /\S+@\S+\.\S+/.test(email) && password.length >= 8;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.createUser({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        isAdmin,
      });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      await onDone({ email: res.row.email, password, kind: 'created' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'creating the user failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add user" onClose={onClose}>
      <div className="form-grid">
        <div className="field">
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label>Email (login)</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
      </div>
      <PasswordField value={password} onChange={setPassword} />
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
        <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
        Admin (can manage users)
      </label>
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose}>Cancel</button>
        <button className="primary" disabled={busy || !valid} onClick={() => void submit()}>
          {busy ? 'Creating…' : 'Create user'}
        </button>
      </div>
    </Modal>
  );
};

const ResetPasswordModal = ({
  user,
  onClose,
  onDone,
}: {
  user: StaffUser;
  onClose: () => void;
  onDone: (cred: IssuedCredential) => Promise<void>;
}) => {
  const [password, setPassword] = useState(generatePassword());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.resetUserPassword(user.id, password);
      await onDone({ email: user.email, password, kind: 'reset' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'resetting the password failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Reset password — ${user.name}`} onClose={onClose}>
      <p className="muted">
        Sets a new password for {user.email} and signs them out everywhere. Hand the new password
        to them yourself — it is only shown once.
      </p>
      <PasswordField value={password} onChange={setPassword} />
      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose}>Cancel</button>
        <button
          className="primary"
          disabled={busy || password.length < 8}
          onClick={() => void submit()}
        >
          {busy ? 'Resetting…' : 'Reset password'}
        </button>
      </div>
    </Modal>
  );
};

export const UsersPage = () => {
  const { user: me } = useAuth();
  const [rows, setRows] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState<StaffUser | null>(null);
  const [issued, setIssued] = useState<IssuedCredential | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.users());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const done = async (cred: IssuedCredential) => {
    setAdding(false);
    setResetting(null);
    setIssued(cred);
    await load();
  };

  return (
    <div className="page">
      <h1>Users</h1>
      <p className="muted">
        Staff logins for this app. Passwords are set here and handed over out-of-band; a reset
        signs the user out of all their sessions.
      </p>
      <div className="toolbar">
        <span className="spacer" />
        <button className="primary" onClick={() => setAdding(true)}>
          + Add user
        </button>
      </div>

      {issued && (
        <div
          style={{
            margin: '12px 0',
            padding: 12,
            border: '1px solid #2e7d32',
            background: '#edf7ed',
            borderRadius: 6,
          }}
        >
          <strong>
            {issued.kind === 'created' ? 'User created.' : 'Password reset.'}
          </strong>{' '}
          Credentials for <code>{issued.email}</code> — copy them now, the password is not shown
          again:
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <code style={{ fontSize: 15 }}>{issued.password}</code>
            <button
              onClick={() => void navigator.clipboard.writeText(issued.password)}
            >
              Copy
            </button>
            <button onClick={() => setIssued(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.name}
                    {me?.id === u.id && <span className="muted"> (you)</span>}
                  </td>
                  <td>{u.email}</td>
                  <td>{u.isAdmin ? <span className="tag ok">Admin</span> : <span className="muted">Staff</span>}</td>
                  <td>{fmtDate(u.createdAt)}</td>
                  <td>
                    <button onClick={() => setResetting(u)}>Reset password…</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && <AddUserModal onClose={() => setAdding(false)} onDone={done} />}
      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={done}
        />
      )}
    </div>
  );
};
